/** Labels and helpers for the order lifecycle state machine. */

const ORDER_STATUS_LABELS = {
  payment_pending: 'Awaiting payment',
  confirmed: 'Confirmed',
  in_fulfillment: 'Out for delivery',
  awaiting_acceptance: 'Waiting for your decision',
  settling: 'Finalizing',
  completed: 'Completed',
  cancelled: 'Cancelled',
  expired: 'Payment expired',
  failed: 'Failed',
  on_hold: 'On hold',
  // legacy
  pending: 'Pending',
  processing: 'Processing',
  shipped: 'Shipped',
  delivered: 'Delivered',
};

export const formatOrderStatusLabel = (status) => {
  if (!status) return '—';
  return ORDER_STATUS_LABELS[status] || String(status).replace(/_/g, ' ');
};

export const isOrderStatusSuccess = (status) => status === 'completed';

export const isOrderStatusWarning = (status) => (
  ['payment_pending', 'awaiting_acceptance', 'in_fulfillment', 'settling', 'on_hold'].includes(status)
);

export const isOrderStatusError = (status) => (
  ['failed', 'cancelled', 'expired'].includes(status)
);

export const getOrderStatusBadgeColor = (status) => {
  if (isOrderStatusSuccess(status)) return '#10b981';
  if (isOrderStatusError(status)) return '#ef4444';
  if (isOrderStatusWarning(status) || status === 'pending') return '#f59e0b';
  return '#6b7280';
};

export const getOrderStatusTextColor = (status) => {
  if (isOrderStatusSuccess(status)) return 'var(--success-color)';
  if (isOrderStatusError(status)) return 'var(--error-color, #dc3545)';
  if (isOrderStatusWarning(status) || status === 'pending') return 'var(--warning-color)';
  return undefined;
};

export const canBuyerDecideOnItem = (item, order) => (
  order?.status !== 'completed'
  && order?.status !== 'cancelled'
  && item?.acceptance_policy === 'try_at_home'
  && item?.acceptance_status === 'awaiting_acceptance'
);

const ACTIVE_DOOR_PREFERENCE_ORDER_STATUSES = new Set([
  'confirmed',
  'in_fulfillment',
  'awaiting_acceptance',
  'settling',
  'completed',
  'on_hold',
]);

export const canBuyerStateDoorPreference = (item, order) => (
  ACTIVE_DOOR_PREFERENCE_ORDER_STATUSES.has(order?.status)
  && item?.acceptance_policy === 'try_at_door'
);

export const hasBuyerDoorPreference = (item) => Boolean(item?.buyer_door_preference);

export const formatBuyerDoorPreferenceLabel = (preference) => {
  if (preference === 'accepted') return 'You kept this item';
  if (preference === 'rejected') return 'You returned this item';
  return null;
};

export const formatAcceptancePolicyLabel = (policy) => {
  if (policy === 'try_at_home') return 'Try at home — accept or reject';
  if (policy === 'try_at_door') return 'Try at door — share your decision';
  return policy || '—';
};

export const formatMatchesDescriptionLabel = (matches) => {
  if (matches === true) return 'Matches description: Yes';
  if (matches === false) return 'Matches description: No';
  return null;
};

export const formatAcceptanceStatusLabel = (status) => {
  const labels = {
    pending: 'Pending delivery',
    awaiting_acceptance: 'Awaiting your decision',
    accepted: 'Accepted',
    rejected: 'Rejected',
  };
  return labels[status] || status || '—';
};

export const formatDecisionDeadline = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString();
};

export const getOrderDisplayId = (order) => (
  order?.order_number || (order?.order_id ? `#${order.order_id}` : null) || (order?.id ? `#${order.id}` : 'Order')
);

/** True when the buyer can rate items (courier delivered or order completed). */
export const canShowOrderItemFeedback = (order, deliveryStatus) => (
  order?.can_leave_feedback === true
  || order?.status === 'completed'
  || String(deliveryStatus || '').toLowerCase() === 'delivered'
);

/** Per-line: eligible order and line was not rejected. */
export const canRateOrderLine = (item, order, deliveryStatus) => {
  if (item?.can_rate === false) return false;
  if (item?.can_rate === true) return true;
  if (!canShowOrderItemFeedback(order, deliveryStatus)) return false;
  return item?.acceptance_status !== 'rejected';
};
